import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { Badge, type ColorlessIconProps, IconAlert, Popup, UserBarItem } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { DASHBOARD_ALERTS_PAGE_SIZE, useDashboardAlertsLazy } from '#entities/dashboardAlert';
import { useDeferred } from '#shared/lib/hooks';
import { DashboardAlertsWindow } from './components/DashboardAlertsWindow';
import { useDashboardAlertsPresentation } from './model/useDashboardAlertsPresentation';
import styles from './DashboardAlertsButton.module.css';

export const DashboardAlertsButton = ({ className }: { className?: string }) => {
    const { value: isManuallyOpen, toggleOff: closeManually, toggleOn: openManually } = useToggle();
    const triggerRef = useRef<HTMLDivElement>(null);
    const deferred = useDeferred();

    const [fetchAlerts, { currentData: alertsData, isError }] = useDashboardAlertsLazy();

    useEffect(() => {
        if (deferred) {
            fetchAlerts({ page: 1, per_page: DASHBOARD_ALERTS_PAGE_SIZE });
        }
    }, [deferred, fetchAlerts]);

    const { criticalAlerts, dismissAutomaticPresentation, isAutomaticallyOpen, startCriticalAlertsCooldown } =
        useDashboardAlertsPresentation(isError ? [] : alertsData?.alerts, isManuallyOpen);
    const isOpen = isManuallyOpen || isAutomaticallyOpen;

    const hasUnreadAlerts = (alertsData?.total_count || 0) > 0;

    const AlertsIcon = useMemo(
        () =>
            forwardRef<SVGSVGElement, ColorlessIconProps>(function AlertsIcon(props, ref) {
                return (
                    <IconAlert
                        {...props}
                        ref={ref}
                        color={hasUnreadAlerts ? 'red' : 'gray'}
                        role={isOpen || hasUnreadAlerts ? 'main' : 'supplementary'}
                    />
                );
            }),
        [isOpen, hasUnreadAlerts]
    );

    const handleClose = useCallback(() => {
        if (isManuallyOpen) {
            startCriticalAlertsCooldown();
        }

        closeManually();
        dismissAutomaticPresentation();
    }, [closeManually, dismissAutomaticPresentation, isManuallyOpen, startCriticalAlertsCooldown]);

    const handleButtonClick = useCallback(() => {
        logEventString('RightSidePanel.AlertsButtonClick', String(isOpen));

        if (isOpen) {
            handleClose();

            return;
        }

        openManually();
    }, [handleClose, isOpen, openManually]);

    return (
        <>
            <Popup
                placement="left-start"
                isOpen={isOpen}
                onClose={handleClose}
                triggerRef={triggerRef}
                isDismissable={false}
                autoFocusProps={{ autoFocus: false }}
            >
                <DashboardAlertsWindow
                    alerts={isAutomaticallyOpen ? criticalAlerts : undefined}
                    isEmpty={alertsData?.total_count === 0}
                    onAction={handleClose}
                    onClose={handleClose}
                />
            </Popup>
            <div title="Открыть панель уведомлений" className={cn(styles.buttonContainer, className)}>
                <UserBarItem ref={triggerRef} icon={AlertsIcon} onClick={handleButtonClick} isActive={isOpen} />
                {hasUnreadAlerts ? <Badge className={styles.badge} color="error" size="12" /> : null}
            </div>
        </>
    );
};
